import React from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    FormControlLabel,
    Switch,
    Stack,
    Box,
    Typography,
    Divider,
    IconButton,
    Tooltip,
    Alert,
} from '@mui/material';
import { Close as CloseIcon, InfoOutlined as InfoIcon } from '@mui/icons-material';

const MatchingOptionsDialog = ({
    open,
    onClose,
    config,
    onChange,
}) => {
    return (
        <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
            <DialogTitle>
                <Box display="flex" alignItems="center" justifyContent="space-between">
                    <Typography variant="h6">Matching Options</Typography>
                    <IconButton onClick={onClose} size="small">
                        <CloseIcon />
                    </IconButton>
                </Box>
            </DialogTitle>
            <DialogContent dividers>
                <Stack spacing={3} sx={{ py: 1 }}>
                    {/* Results Count */}
                    <Box>
                        <Box display="flex" alignItems="center" gap={1} mb={1}>
                            <Typography variant="subtitle2">Results Display</Typography>
                            <Tooltip title="Number of top matches to return from algorithmic search.">
                                <InfoIcon sx={{ fontSize: 16, color: 'text.secondary', cursor: 'pointer' }} />
                            </Tooltip>
                        </Box>
                        <Stack spacing={2}>
                            <TextField
                                fullWidth
                                label="Standard Top N Results"
                                type="number"
                                size="small"
                                value={config.topN}
                                onChange={(e) => onChange('topN', parseInt(e.target.value, 10) || 5)}
                                inputProps={{ min: 1, max: 20 }}
                            />
                            <TextField
                                fullWidth
                                label="Custom Top N Results"
                                type="number"
                                size="small"
                                value={config.customTopN}
                                onChange={(e) => onChange('customTopN', parseInt(e.target.value, 10) || 5)}
                                inputProps={{ min: 1, max: 20 }}
                                disabled={!config.includeCustom}
                                helperText={config.includeCustom ? 'Used for the Custom Matches section.' : 'Enable custom reports to configure this section.'}
                            />
                            <TextField
                                fullWidth
                                label="Standard Other Theme Top N"
                                type="number"
                                size="small"
                                value={config.standardOtherThemeTopN}
                                onChange={(e) => onChange('standardOtherThemeTopN', parseInt(e.target.value, 10) || 5)}
                                inputProps={{ min: 1, max: 20 }}
                                helperText="Used for the Standard Other Themes section."
                            />
                            <TextField
                                fullWidth
                                label="Final Match Threshold (%)"
                                type="number"
                                size="small"
                                value={config.finalMatchThreshold}
                                onChange={(e) => onChange('finalMatchThreshold', parseFloat(e.target.value) || 0)}
                                inputProps={{ min: 0, max: 100, step: 0.1 }}
                                helperText="Only final report matches at or above this overlap score are shown."
                            />
                        </Stack>
                    </Box>

                    <Divider />

                    {/* Basic Toggles */}
                    <Box>
                        <Typography variant="subtitle2" gutterBottom>Search Filters</Typography>
                        <Stack spacing={1}>
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={config.sameThemeOnly}
                                        onChange={(e) => onChange('sameThemeOnly', e.target.checked)}
                                    />
                                }
                                label="Match Same Themes Only"
                            />
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={config.globalThemeOnly}
                                        onChange={(e) => onChange('globalThemeOnly', e.target.checked)}
                                    />
                                }
                                label="Match Global Themes Only"
                            />
                            {config.sameThemeOnly && config.globalThemeOnly && (
                                <Alert severity="warning" sx={{ mt: 1 }}>
                                    Only one theme filter can be applied at a time. Global themes will be used.
                                </Alert>
                            )}
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={config.ignoreCommon}
                                        onChange={(e) => onChange('ignoreCommon', e.target.checked)}
                                    />
                                }
                                label="Ignore Common Columns"
                            />
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={config.ignorePii}
                                        onChange={(e) => onChange('ignorePii', e.target.checked)}
                                    />
                                }
                                label="Ignore PII Columns"
                            />
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={config.includeCustom}
                                        onChange={(e) => onChange('includeCustom', e.target.checked)}
                                    />
                                }
                                label="Include Custom Reports"
                            />
                        </Stack>
                    </Box>

                </Stack>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} color="primary" variant="contained">
                    Apply & Close
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default MatchingOptionsDialog;
